import {
  collection,
  doc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Coupon, CouponCreateRequest, CouponUsage, CouponFilters } from '../types/coupon';
import { csvCell } from '../utils/csvExport';

class CouponService {
  private couponsCollection = 'coupons';
  private usageCollection = 'couponUsage';

  // Generate random coupon code
  private generateRandomCode(prefix = 'PICKUP', length = 6): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = prefix;
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  // Check if coupon code already exists
  private async isCodeUnique(code: string): Promise<boolean> {
    const q = query(
      collection(db, this.couponsCollection),
      where('code', '==', code.toUpperCase())
    );
    const snapshot = await getDocs(q);
    return snapshot.empty;
  }

  // Generate unique coupon code
  private async generateUniqueCode(prefix = 'PICKUP', length = 6): Promise<string> {
    let code: string;
    let attempts = 0;
    const maxAttempts = 10;

    do {
      code = this.generateRandomCode(prefix, length);
      attempts++;
      
      if (attempts > maxAttempts) {
        // Increase length if too many collisions
        length++;
        attempts = 0;
      }
    } while (!(await this.isCodeUnique(code)) && attempts <= maxAttempts);

    return code;
  }

  // Create single or multiple coupons
  async createCoupons(request: CouponCreateRequest, adminId: string): Promise<Coupon[]> {
    const createdCoupons: Coupon[] = [];
    const batch = writeBatch(db);
    const now = new Date().toISOString();

    try {
      if (request.generateType === 'custom') {
        // Create single custom coupon
        if (!request.customCode) {
          throw new Error('Custom code is required for custom coupon generation');
        }

        const upperCode = request.customCode.toUpperCase();
        const isUnique = await this.isCodeUnique(upperCode);
        
        if (!isUnique) {
          throw new Error(`Coupon code "${upperCode}" already exists`);
        }

        const couponRef = doc(collection(db, this.couponsCollection));
        const coupon: Coupon = {
          id: couponRef.id,
          code: upperCode,
          title: request.title,
          description: request.description,
          type: request.type,
          value: request.value,
          minOrderValue: request.minOrderValue,
          ...(request.maxDiscount !== undefined && { maxDiscount: request.maxDiscount }),
          usageLimit: request.usageLimit,
          usageCount: 0,
          userUsageLimit: request.userUsageLimit,
          validFrom: request.validFrom,
          validUntil: request.validUntil,
          isFeatured: request.isFeatured,
          isActive: true,
          imageUrl: request.imageUrl,
          applicableServices: request.applicableServices || [],
          applicableUserTypes: request.applicableUserTypes || [],
          applicableLocations: request.applicableLocations || [],
          termsAndConditions: request.termsAndConditions,
          createdAt: now,
          updatedAt: now,
          createdBy: adminId
        };

        batch.set(couponRef, coupon);
        createdCoupons.push(coupon);

      } else {
        // Create multiple random coupons
        const quantity = request.quantity || 1;
        const prefix = request.codePrefix || 'PICKUP';

        for (let i = 0; i < quantity; i++) {
          const code = await this.generateUniqueCode(prefix);
          const couponRef = doc(collection(db, this.couponsCollection));
          
          const coupon: Coupon = {
            id: couponRef.id,
            code,
            title: `${request.title} ${quantity > 1 ? `#${i + 1}` : ''}`,
            description: request.description,
            type: request.type,
            value: request.value,
            minOrderValue: request.minOrderValue,
            ...(request.maxDiscount !== undefined && { maxDiscount: request.maxDiscount }),
            usageLimit: request.usageLimit,
            usageCount: 0,
            userUsageLimit: request.userUsageLimit,
            validFrom: request.validFrom,
            validUntil: request.validUntil,
            isFeatured: request.isFeatured,
            isActive: true,
            imageUrl: request.imageUrl,
            applicableServices: request.applicableServices || [],
            applicableUserTypes: request.applicableUserTypes || [],
            applicableLocations: request.applicableLocations || [],
            termsAndConditions: request.termsAndConditions,
            createdAt: now,
            updatedAt: now,
            createdBy: adminId
          };

          batch.set(couponRef, coupon);
          createdCoupons.push(coupon);
        }
      }

      await batch.commit();
      return createdCoupons;

    } catch (error) {
      console.error('Error creating coupons:', error);
      throw error;
    }
  }

  // Get all coupons with filters
  async getCoupons(filters?: CouponFilters): Promise<Coupon[]> {
    try {
      console.log('getCoupons called with filters:', filters);

      // Issue #271: Only fetch non-deleted coupons (soft delete support)
      // Start with a basic query that filters out soft-deleted coupons and orders by creation date
      const q = query(
        collection(db, this.couponsCollection),
        where('isActive', '!=', false),
        orderBy('isActive', 'desc'),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      let coupons = snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Coupon));
      
      console.log('Initial coupons count:', coupons.length);

      // Apply filters client-side to avoid Firestore compound query limitations
      if (filters?.type) {
        coupons = coupons.filter(coupon => coupon.type === filters.type);
        console.log(`After type filter (${filters.type}):`, coupons.length);
      }
      
      if (filters?.isActive !== undefined) {
        coupons = coupons.filter(coupon => coupon.isActive === filters.isActive);
        console.log(`After active filter (${filters.isActive}):`, coupons.length);
      }
      
      if (filters?.isFeatured !== undefined) {
        coupons = coupons.filter(coupon => coupon.isFeatured === filters.isFeatured);
        console.log(`After featured filter (${filters.isFeatured}):`, coupons.length);
      }

      // Apply search filter
      if (filters?.search) {
        const search = filters.search.toLowerCase();
        coupons = coupons.filter(coupon => 
          coupon.code.toLowerCase().includes(search) ||
          coupon.title.toLowerCase().includes(search) ||
          coupon.description.toLowerCase().includes(search)
        );
        console.log(`After search filter (${filters.search}):`, coupons.length);
      }

      // Apply usage filters
      if (filters?.minUsage !== undefined) {
        coupons = coupons.filter(coupon => coupon.usageCount >= filters.minUsage!);
      }

      if (filters?.maxUsage !== undefined) {
        coupons = coupons.filter(coupon => coupon.usageCount <= filters.maxUsage!);
      }

      // Apply date filters
      if (filters?.validFrom) {
        coupons = coupons.filter(coupon => new Date(coupon.validFrom) >= new Date(filters.validFrom!));
      }

      if (filters?.validUntil) {
        coupons = coupons.filter(coupon => new Date(coupon.validUntil) <= new Date(filters.validUntil!));
      }

      console.log('Final filtered coupons count:', coupons.length);
      return coupons;
    } catch (error) {
      console.error('Error fetching coupons:', error);
      throw error;
    }
  }

  // Get single coupon
  async getCoupon(id: string): Promise<Coupon | null> {
    try {
      const docSnap = await getDoc(doc(db, this.couponsCollection, id));
      if (docSnap.exists()) {
        return { ...docSnap.data(), id: docSnap.id } as Coupon;
      }
      return null;
    } catch (error) {
      console.error('Error fetching coupon:', error);
      throw error;
    }
  }

  // Update coupon
  async updateCoupon(couponId: string, updates: Partial<Coupon>): Promise<void> {
    try {
      const couponRef = doc(db, this.couponsCollection, couponId);
      
      // Clean the updates object to remove undefined values
      const cleanUpdates: any = {
        ...updates,
        updatedAt: new Date().toISOString()
      };

      // Remove undefined values to prevent Firestore errors
      Object.keys(cleanUpdates).forEach(key => {
        if (cleanUpdates[key] === undefined) {
          delete cleanUpdates[key];
        }
      });

      await updateDoc(couponRef, cleanUpdates);
    } catch (error) {
      console.error('Error updating coupon:', error);
      throw error;
    }
  }

  // Delete coupon - Issue #271: Soft delete implementation
  async deleteCoupon(couponId: string): Promise<void> {
    try {
      // Issue #271: Soft delete - set isActive to false instead of permanent deletion
      const couponRef = doc(db, this.couponsCollection, couponId);
      await updateDoc(couponRef, {
        isActive: false,
        updatedAt: new Date().toISOString()
      });
      console.log(`✅ Issue #271: Coupon ${couponId} soft deleted (isActive: false)`);
    } catch (error) {
      console.error('❌ Issue #271: Error soft deleting coupon:', error);
      throw error;
    }
  }

  // Get coupon usage history
  async getCouponUsage(couponId: string): Promise<CouponUsage[]> {
    try {
      const q = query(
        collection(db, this.usageCollection),
        where('couponId', '==', couponId),
        orderBy('usedAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as CouponUsage));
    } catch (error) {
      console.error('Error fetching coupon usage:', error);
      throw error;
    }
  }

  // Helper to check if a coupon is truly active (isActive AND not expired AND started)
  private isCouponActive(coupon: Coupon): boolean {
    if (!coupon.isActive) return false;

    const now = new Date();
    const validFrom = new Date(coupon.validFrom);
    const validUntil = new Date(coupon.validUntil);

    // Coupon is active if isActive is true AND current date is within valid range
    return now >= validFrom && now <= validUntil;
  }

  // Get analytics data
  async getCouponAnalytics() {
    try {
      // Get all coupons
      const couponsSnapshot = await getDocs(collection(db, this.couponsCollection));
      const coupons = couponsSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Coupon));

      // Try to get usage records, but don't fail if collection doesn't exist
      let usageRecords: CouponUsage[] = [];
      try {
        const usageSnapshot = await getDocs(collection(db, this.usageCollection));
        usageRecords = usageSnapshot.docs.map(doc => doc.data() as CouponUsage);
      } catch (usageError) {
        console.warn('Coupon usage collection not found or accessible, using empty array');
      }

      // Create a Set of existing coupon IDs for quick lookup
      const existingCouponIds = new Set(coupons.map(c => c.id));

      // Filter usage records to only include existing (non-deleted) coupons
      const validUsageRecords = usageRecords.filter(usage => existingCouponIds.has(usage.couponId));

      const totalCoupons = coupons.length;
      // Count only truly active coupons (isActive AND not expired AND started)
      const activeCoupons = coupons.filter(c => this.isCouponActive(c)).length;
      const featuredCoupons = coupons.filter(c => c.isFeatured && this.isCouponActive(c)).length;

      // Calculate total usage from usageCount in existing coupons only
      const totalUsageFromCoupons = coupons.reduce((sum, c) => sum + (c.usageCount || 0), 0);
      // Use the higher value between valid usageRecords count and sum of usageCount
      const totalUsage = Math.max(validUsageRecords.length, totalUsageFromCoupons);
      // Calculate discount only from usage records of existing coupons
      const totalDiscountGiven = validUsageRecords.reduce((sum, usage) => sum + (usage.discountAmount || 0), 0);

      // Calculate top coupons (only for existing coupons)
      const couponUsageMap = new Map<string, { usage: number, discount: number }>();
      validUsageRecords.forEach(usage => {
        const existing = couponUsageMap.get(usage.couponId) || { usage: 0, discount: 0 };
        couponUsageMap.set(usage.couponId, {
          usage: existing.usage + 1,
          discount: existing.discount + usage.discountAmount
        });
      });

      const topCoupons = Array.from(couponUsageMap.entries())
        .map(([couponId, stats]) => {
          const coupon = coupons.find(c => c.id === couponId);
          return {
            couponId,
            code: coupon?.code || '',
            title: coupon?.title || '',
            usageCount: stats.usage,
            discountGiven: stats.discount
          };
        })
        .filter(tc => tc.code) // Only include coupons that still exist
        .sort((a, b) => b.usageCount - a.usageCount)
        .slice(0, 10);

      return {
        totalCoupons,
        activeCoupons,
        featuredCoupons,
        totalUsage,
        totalDiscountGiven,
        topCoupons
      };

    } catch (error) {
      console.error('Error getting coupon analytics:', error);
      throw error;
    }
  }

  // Helper to get real-time coupon status for display/export
  private getCouponStatus(coupon: Coupon): string {
    const now = new Date();
    const validFrom = new Date(coupon.validFrom);
    const validUntil = new Date(coupon.validUntil);

    if (!coupon.isActive) {
      return 'Inactive';
    } else if (now < validFrom) {
      return 'Scheduled';
    } else if (now > validUntil) {
      return 'Expired';
    } else {
      return 'Active';
    }
  }

  // Export coupons
  async exportCoupons(filters?: CouponFilters): Promise<string> {
    const coupons = await this.getCoupons(filters);
    const headers = ['Code', 'Title', 'Type', 'Value', 'Min Order', 'Usage', 'Limit', 'Status', 'Valid From', 'Valid Until'];

    const csvRows = [headers.join(',')];

    coupons.forEach(coupon => {
      // Calculate real-time status based on validUntil date
      const status = this.getCouponStatus(coupon);

      // csvCell quotes/escapes every field (code and status previously
      // weren't — a code containing a comma broke the row shape) and
      // neutralises a leading =/+/-/@ so a coupon title can't turn into a
      // spreadsheet formula when an admin opens the export.
      const row = [
        csvCell(coupon.code),
        csvCell(coupon.title),
        csvCell(coupon.type),
        csvCell(coupon.value),
        csvCell(coupon.minOrderValue),
        csvCell(coupon.usageCount),
        csvCell(coupon.usageLimit),
        csvCell(status),
        csvCell(new Date(coupon.validFrom).toLocaleDateString()),
        csvCell(new Date(coupon.validUntil).toLocaleDateString())
      ];
      csvRows.push(row.join(','));
    });

    return csvRows.join('\n');
  }
}

const couponService = new CouponService();
export default couponService;